import type { ClaimEvidence, DeckIntelligence } from '../types';

const CLAIM_EVIDENCE_VALUES: ClaimEvidence[] = [
  'supported',
  'weak',
  'likely_false',
  'speculative',
];

function isClaimEvidence(v: unknown): v is ClaimEvidence {
  return typeof v === 'string' && CLAIM_EVIDENCE_VALUES.includes(v as ClaimEvidence);
}

function asString(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined;
}

function asStringArray(v: unknown): string[] | undefined {
  if (!Array.isArray(v)) return undefined;
  const items = v.filter((x): x is string => typeof x === 'string');
  return items.length ? items : undefined;
}

/** Stable short fingerprint of deck OCR text for stale analysis detection. */
export function deckIntelligenceSourceFingerprint(text: string): string {
  let hash = 2166136261;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

function clampScore1To10(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.min(10, Math.max(1, Math.round(value)));
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number.parseInt(value.trim(), 10);
    if (Number.isFinite(parsed)) {
      return Math.min(10, Math.max(1, parsed));
    }
  }
  return undefined;
}

function normalizeSectionScores(raw: unknown): DeckIntelligence['sectionScores'] {
  if (!raw || typeof raw !== 'object') return undefined;

  const row = raw as Record<string, unknown>;
  const keys = ['team', 'market', 'product', 'businessModel', 'traction', 'funding'] as const;
  const scores = {} as NonNullable<DeckIntelligence['sectionScores']>;

  for (const key of keys) {
    const score = clampScore1To10(row[key]);
    if (score == null) return undefined;
    scores[key] = score;
  }

  return scores;
}

function normalizeCompetitors(raw: unknown): DeckIntelligence['competitors'] {
  if (!Array.isArray(raw)) return undefined;

  const competitors = raw
    .filter((item) => item && typeof item === 'object')
    .map((item) => {
      const row = item as Record<string, unknown>;
      const name = asString(row.name)?.trim();
      if (!name) return null;

      const websiteUrl = asString(row.websiteUrl)?.trim();
      const note = asString(row.note)?.trim();
      const marketStrength = clampScore1To10(row.marketStrength);
      if (marketStrength == null) return null;

      return {
        name,
        ...(websiteUrl ? { websiteUrl } : {}),
        marketStrength,
        ...(note ? { note } : {}),
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  return competitors.length ? competitors : undefined;
}

/** Parse and validate `deckIntelligence` from Firestore; returns undefined when invalid. */
export function normalizeDeckIntelligence(raw: unknown): DeckIntelligence | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const x = raw as Record<string, unknown>;

  const analyzedAt = asString(x.analyzedAt);
  const sourceTextFingerprint = asString(x.sourceTextFingerprint);
  const model = asString(x.model);
  const team = asString(x.team);
  const market = asString(x.market);
  const product = asString(x.product);
  const businessModel = asString(x.businessModel);
  const traction = asString(x.traction);
  const funding = asString(x.funding);
  const competition = asString(x.competition);

  if (
    !analyzedAt ||
    !sourceTextFingerprint ||
    !model ||
    !team ||
    !market ||
    !product ||
    !businessModel ||
    !traction ||
    !funding ||
    !competition
  ) {
    return undefined;
  }

  const risks = Array.isArray(x.risks)
    ? x.risks
        .filter((r) => r && typeof r === 'object')
        .map((r) => {
          const row = r as Record<string, unknown>;
          const description = asString(row.description);
          const riskEvaluation = asString(row.riskEvaluation);
          if (!description || !riskEvaluation) return null;
          return { description, riskEvaluation };
        })
        .filter((r): r is { description: string; riskEvaluation: string } => r !== null)
    : [];

  const claims = Array.isArray(x.claims)
    ? x.claims
        .filter((c) => c && typeof c === 'object')
        .map((c) => {
          const row = c as Record<string, unknown>;
          const name = asString(row.name);
          const evidenceEvaluation = row.evidenceEvaluation;
          if (!name || !isClaimEvidence(evidenceEvaluation)) return null;
          return { name, evidenceEvaluation };
        })
        .filter((c): c is { name: string; evidenceEvaluation: ClaimEvidence } => c !== null)
    : [];

  const missing = asStringArray(x.missing) ?? [];

  const inverseRaw = x.inversePitch;
  if (!inverseRaw || typeof inverseRaw !== 'object') return undefined;
  const inv = inverseRaw as Record<string, unknown>;
  const whyFailure = asString(inv.whyFailure);
  const failureReason = asString(inv.failureReason);
  const whySuccess = asString(inv.whySuccess);
  const successReason = asString(inv.successReason);
  if (!whyFailure || !failureReason || !whySuccess || !successReason) return undefined;

  const memoRaw = x.investorMemo;
  if (!memoRaw || typeof memoRaw !== 'object') return undefined;
  const memo = memoRaw as Record<string, unknown>;
  const bullCase = asString(memo.bullCase);
  const bearCase = asString(memo.bearCase);
  const devilsAdvocate = asString(memo.devilsAdvocate);
  const contrarianOpportunity = asString(memo.contrarianOpportunity);
  if (!bullCase || !bearCase || !devilsAdvocate || !contrarianOpportunity) return undefined;

  const analysisRaw = x.analysis;
  if (!analysisRaw || typeof analysisRaw !== 'object') return undefined;
  const analysisObj = analysisRaw as Record<string, unknown>;
  const top3UnicornReasons = asStringArray(analysisObj.top3UnicornReasons);
  const top3DeathReasons = asStringArray(analysisObj.top3DeathReasons);
  const mostDangerousAssumption = asString(analysisObj.mostDangerousAssumption);
  const hundredXRequirements = asString(analysisObj.hundredXRequirements);
  const doubleDownEvidence = asString(analysisObj.doubleDownEvidence);
  if (
    !top3UnicornReasons ||
    !top3DeathReasons ||
    !mostDangerousAssumption ||
    !hundredXRequirements ||
    !doubleDownEvidence
  ) {
    return undefined;
  }

  const historicallySimilarCompanies = Array.isArray(x.historicallySimilarCompanies)
    ? x.historicallySimilarCompanies
        .filter((c) => c && typeof c === 'object')
        .map((c) => {
          const row = c as Record<string, unknown>;
          const name = asString(row.name);
          const similarity = asString(row.similarity);
          if (!name || !similarity) return null;
          return { name, similarity };
        })
        .filter((c): c is { name: string; similarity: string } => c !== null)
    : [];

  return {
    analyzedAt,
    sourceTextFingerprint,
    model,
    team,
    market,
    product,
    businessModel,
    traction,
    funding,
    competition,
    sectionScores: normalizeSectionScores(x.sectionScores),
    competitors: normalizeCompetitors(x.competitors),
    risks,
    claims,
    missing,
    inversePitch: {
      whyFailure,
      failureReason,
      whySuccess,
      successReason,
    },
    investorMemo: {
      bullCase,
      bearCase,
      devilsAdvocate,
      contrarianOpportunity,
    },
    analysis: {
      top3UnicornReasons,
      top3DeathReasons,
      mostDangerousAssumption,
      hundredXRequirements,
      doubleDownEvidence,
    },
    historicallySimilarCompanies,
  };
}
